import { useEffect, useRef, useState } from 'react'
import { Linking, Platform, Text, View } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { CameraView, useCameraPermissions, type CameraCapturedPicture } from 'expo-camera'
import { Camera, Check, X, Zap } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Pressable } from '@/components/ui/Pressable'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { toast } from '@/lib/toast'

export interface LiveCameraCapture {
  uri: string
  width: number
  height: number
  capturedAt: string
  source: 'tago_live_camera'
}

interface LiveCameraModalProps {
  visible: boolean
  title: string
  helper?: string
  frameAspectRatio?: number
  /** e.g. "Foto 4 de 17" — shown in-frame while the session walks a checklist. */
  progressLabel?: string
  /** Exterior coverage evidence must be captured with usable natural/lighted exposure. */
  requiresDaylight?: boolean
  onCancel: () => void
  onCapture: (capture: LiveCameraCapture) => void
}

export function LiveCameraModal({
  visible,
  title,
  helper,
  frameAspectRatio = 4 / 3,
  progressLabel,
  requiresDaylight = false,
  onCancel,
  onCapture,
}: LiveCameraModalProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const cameraRef = useRef<CameraView>(null)
  const [permission, requestPermission] = useCameraPermissions()
  const [isReady, setIsReady] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  // Torch: the chassis/VIN/odometer shots the helper text calls "legible" are the
  // darkest ones in the checklist, and there was no way to add light.
  const [torchOn, setTorchOn] = useState(false)

  // Reset on every open. RN's Modal renders null while hidden on Android, so
  // CameraView remounts for each of the 17 shots — but this state did not, and
  // any capture that threw or was interrupted left `isCapturing` true FOREVER.
  // The shutter then stayed disabled, mid-checklist, on the step hosts already
  // dread, with no way out but killing the app. `isReady` had the mirror problem:
  // stale-true against a camera that has not called onCameraReady yet.
  useEffect(() => {
    if (visible) {
      setIsCapturing(false)
      setIsReady(false)
      setTorchOn(false)
    }
  }, [visible])

  const capture = async () => {
    if (!cameraRef.current || isCapturing || !isReady) return

    // A timestamp alone never decides whether a photo is acceptable on the
    // server, but it gives the host an immediate, frictionless correction at
    // the camera. The server still measures the stored bytes before submission.
    const localHour = new Date().getHours()
    if (requiresDaylight && (localHour < 7 || localHour >= 17) && !torchOn) {
      toast.error(t('listing.camera_need_light'))
      return
    }

    setIsCapturing(true)
    try {
      const capturedAt = new Date().toISOString()
      // EXIF must stay ON. Android camera sensors write a landscape buffer and
      // put the real orientation in the tag. Turning the tag off dropped it,
      // ImageManipulator then saved orientation-1 JPEGs, and listing galleries
      // showed the car on its side (Ford Escape 01M2H00GB7S7JMV1HA0BRDW7DT).
      // The listing compressor re-encodes JPEG, so GPS does not persist in the
      // stored object — we only need the tag long enough to bake pixels.
      const picture = (await cameraRef.current.takePictureAsync({
        quality: 0.86,
        exif: true,
        skipProcessing: false,
      })) as CameraCapturedPicture | undefined

      if (!picture?.uri) return

      onCapture({
        uri: picture.uri,
        width: picture.width,
        height: picture.height,
        capturedAt,
        source: 'tago_live_camera',
      })
    } finally {
      setIsCapturing(false)
    }
  }

  const permissionGranted = permission?.granted === true

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onCancel}
      // The camera preview is a native view that consumes its own touches, so
      // every tap on it would look dead to the liveness probe.
    >
      <View style={{ flex: 1, backgroundColor: '#050506' }}>
        {permissionGranted ? (
          <CameraView
            ref={cameraRef}
            facing="back"
            mode="picture"
            enableTorch={torchOn}
            style={{ flex: 1 }}
            onCameraReady={() => setIsReady(true)}
          >
            <View
              style={{
                flex: 1,
                justifyContent: 'space-between',
                paddingHorizontal: SPACING.lg,
                paddingTop: Platform.OS === 'ios' ? 60 : SPACING.xl,
                paddingBottom: Platform.OS === 'ios' ? 42 : SPACING.xl,
                backgroundColor: 'rgba(0,0,0,0.16)',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: SPACING.md,
                }}
              >
                <Pressable
                  onPress={onCancel}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.cancel')}
                  testID="camera.cancel"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 22,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.44)',
                  }}
                >
                  <X size={22} color="#FFFFFF" strokeWidth={2} />
                </Pressable>

                {/* Light for the dark shots. The helper text asks the host to make
                    the chassis number "legible"; without this there was no way to
                    do it, and Qualitas rejects an unreadable one — costing a full
                    17-shot redo. */}
                <Pressable
                  onPress={() => setTorchOn((on) => !on)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: torchOn }}
                  accessibilityLabel={t('listing.camera_torch')}
                  testID="camera.torch"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 22,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: torchOn ? '#FFFFFF' : 'rgba(0,0,0,0.44)',
                  }}
                >
                  <Zap size={20} color={torchOn ? '#050506' : '#FFFFFF'} strokeWidth={2} />
                </Pressable>
              </View>

              {/* Instruction and frame travel together, so the text sits directly
                  above the box it describes instead of being flung to the far
                  edge by the column's space-between. */}
              <View style={{ gap: SPACING.md }}>
                {/* The instruction belongs OVER the frame, centred.
                  Right-aligned in the corner it read as chrome, so the host did
                  not notice it change between shots — and what changes on every
                  one of the 17 is precisely the only thing they need to read. */}
                <View style={{ alignItems: 'center', gap: SPACING.xs }}>
                  {progressLabel ? (
                    <View
                      style={{
                        paddingVertical: 2,
                        paddingHorizontal: SPACING.sm,
                        borderRadius: RADIUS.full,
                        backgroundColor: COLORS.coral.dark,
                      }}
                    >
                      <Text style={{ fontFamily: FONTS.bodyBold, color: '#FFFFFF', fontSize: 12 }}>
                        {progressLabel}
                      </Text>
                    </View>
                  ) : null}
                  <View
                    style={{
                      maxWidth: '100%',
                      paddingVertical: SPACING.xs,
                      paddingHorizontal: SPACING.md,
                      borderRadius: RADIUS.full,
                      backgroundColor: 'rgba(0,0,0,0.62)',
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: FONTS.bodyBold,
                        color: '#FFFFFF',
                        fontSize: 16,
                        textAlign: 'center',
                      }}
                      numberOfLines={2}
                    >
                      {title}
                    </Text>
                  </View>
                  {helper ? (
                    <Text
                      style={{
                        maxWidth: 320,
                        fontFamily: FONTS.body,
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: 13,
                        lineHeight: 18,
                        textAlign: 'center',
                      }}
                      numberOfLines={3}
                    >
                      {helper}
                    </Text>
                  ) : null}
                </View>

                <View
                  pointerEvents="none"
                  style={{
                    alignSelf: 'center',
                    width: '90%',
                    aspectRatio: frameAspectRatio,
                    borderWidth: 2,
                    borderColor: 'rgba(255,255,255,0.88)',
                    borderRadius: RADIUS.lg,
                  }}
                />
              </View>

              <View style={{ alignItems: 'center', gap: SPACING.md }}>
                <Pressable
                  onPress={() => void capture()}
                  accessibilityRole="button"
                  accessibilityLabel={t('listing.capture_live_photo')}
                  testID="camera.shutter"
                  disabled={!isReady || isCapturing}
                  style={{
                    width: 78,
                    height: 78,
                    borderRadius: 39,
                    borderWidth: 4,
                    borderColor: '#FFFFFF',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: !isReady || isCapturing ? 0.55 : 1,
                  }}
                >
                  <View
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 29,
                      backgroundColor: COLORS.coral.dark,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isCapturing ? (
                      <Camera size={24} color="#FFFFFF" strokeWidth={2} />
                    ) : (
                      <Check size={28} color="#FFFFFF" strokeWidth={2.4} />
                    )}
                  </View>
                </Pressable>
              </View>
            </View>
          </CameraView>
        ) : (
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING.xl,
              gap: SPACING.lg,
              backgroundColor: colors.background,
            }}
          >
            <Camera size={44} color={colors.textSecondary} strokeWidth={1.6} />
            <Text
              style={{
                textAlign: 'center',
                fontFamily: FONTS.bodyBold,
                fontSize: 18,
                color: colors.textPrimary,
              }}
            >
              {t('listing.camera_permission_denied')}
            </Text>
            {/* "No volver a preguntar" is a TOTAL dead end otherwise: on Android
                with canAskAgain === false, requestPermission() resolves instantly
                as denied and re-renders this identical screen. With no gallery
                fallback (Qualitas requires live capture) the host cannot take a
                single photo, ever. Send them to system settings instead — the
                only place the decision can actually be reversed. */}
            <Button
              title={
                permission?.canAskAgain === false
                  ? t('listing.camera_permission_settings_cta')
                  : t('checkin.camera_permission_cta')
              }
              onPress={() => {
                if (permission?.canAskAgain === false) {
                  void Linking.openSettings()
                  return
                }
                void requestPermission()
              }}
              testID="live-camera.permission"
            />
            <Button
              title={t('common.cancel')}
              variant="secondary"
              onPress={onCancel}
              testID="live-camera.cancel"
            />
          </View>
        )}
      </View>
    </AppModal>
  )
}
